/**
 * settlement_arrange.ts — the ARRANGEMENT rules of a settlement's assembly,
 * pure and shared (2026-09-23): the module (CommonsSettlementsModule), the
 * far-tier bake (settlement_proxy_bake) and the layout audit
 * (verify/settlement-layout) all size and test placements through these, so
 * a rule changed here changes the build, the silhouette and the gate together.
 *
 * THE LOT LAW. A building is scaled INTO its pad (lib/lot_envelope — the lot
 * minus the roads' clearances and a side gap), never to a tier's lattice width
 * around the pad's centre: the height band sets how tall it wants to be, the
 * lot decides what it may be. Two lots never share ground, so two buildings
 * never meet; a lot never reaches a carriageway, so no building does.
 *
 * THE HANDEDNESS LAW. The planner's `orientation` is atan2 over the frontage
 * direction (cos θ, sin θ); three's `rotation.y = θ` turns local +x to
 * (cos θ, −sin θ). Every pad was drawn at the MIRRORED angle — right only on
 * axis-aligned streets, perpendicular to a 45° one. `padYaw` is the one
 * conversion.
 *
 * THE FOOTPRINT TEST. Overlap is judged on the drawn footprints (a
 * separating-axis test on the yawed boxes), not on a circle of the model's
 * longest half-extent — that circle blocked every rowhouse beside another and
 * stepped 42 % of the world's buildings down the shrink ladder.
 */
import type { PrefabMetrics } from './settlement_proxy_bake.js';
import type { CityPlan } from 'enginex/city/config.js';
import { parcelEnvelope, segDist, type LotRoad } from './lot_envelope.js';
import { h01 } from './districts.js';

// Mirrors of CommonsSettlementsModule's constants (the assembly is the authority).
export const CIV_SCALE_MIN = 0.1;
export const CIV_SCALE_MAX = 8;
export const CIV_LOT_W: Record<string, number> = { city: 60, town: 34, hamlet: 20, capital: 160, district: 60, circuit: 34 };
export const CIV_LANDMARK_H: Record<string, number> = { city: 340, town: 250, hamlet: 90, capital: 520, district: 340, circuit: 120 };
export const CIV_LANDMARK_LOT_W: Record<string, number> = { city: 160, town: 100, hamlet: 50, capital: 220, district: 160, circuit: 100 };
export const LANDMARK_SCALE = 8;
export const BUILD_FILL = 0.88;
export const BUILD_MIN_SCALE = 5;
export const BUILD_MAX_SCALE = 26;
export const BUILD_SCALE_CAP = 180;
const CIV_PREFIX = 'structure:civ';
/** The smallest scale the pad cap may impose (a degenerate pad); a 6 m pad on the widest model is 0.067. */
export const PAD_CAP_FLOOR = 0.03;
/** A landmark's bounding circle fills at most this share of its park's envelope — a monument, never the whole square. */
export const LANDMARK_PARK_SHARE = 0.72;

/** The yaw three draws a pad's building at, from the planner's orientation (THE HANDEDNESS LAW). */
export function padYaw(orientation: number): number { return -orientation; }

/**
 * The assembly's scale for a BUILDING: the height band (civ: tH ÷ the model's
 * height, clamped) capped INTO the pad on both axes and by the tier's lattice
 * width; legacy (normalized) models fit the pad with the historical clamps,
 * then the same pad cap. `pw`/`pd` are the pad's width (along the frontage)
 * and depth — the model's x and z after `padYaw`.
 */
export function buildingScale(key: string, tier: string, tH: number | undefined, pw: number, pd: number, m: PrefabMetrics, legacyMult = 1): number {
  const glbW = Math.max(0.2, m.w), glbD = Math.max(0.2, m.d), glbH = Math.max(0.2, m.h);
  // The pad cap BINDS: a 90 m hall on a 6 m hamlet lot is a 6 m shed, never a 10.8 m one 2.4 m into the street
  // (the 0.12 floor the lattice rule carried put 355 hamlet buildings over their roads). The floor only
  // guards a degenerate pad.
  const padCap = Math.max(PAD_CAP_FLOOR, Math.min(pw / glbW, pd / glbD));
  if (tH !== undefined) {
    let s = Math.max(CIV_SCALE_MIN, Math.min(CIV_SCALE_MAX, tH / glbH));
    s = Math.min(s, Math.max(0.12, (CIV_LOT_W[tier] ?? 20) / Math.max(glbW, glbD)));
    return Math.min(s, padCap);
  }
  const fit = BUILD_FILL * padCap;
  const base = Math.min(BUILD_SCALE_CAP, Math.max(BUILD_MIN_SCALE, Math.min(BUILD_MAX_SCALE, fit)) * legacyMult);
  return Math.min(base, padCap);
}

/** A landmark's bounding-circle radius at a scale (any yaw fits inside a square of twice this). */
export function landmarkRadius(m: PrefabMetrics, scale: number): number {
  return (Math.hypot(Math.max(0.2, m.w), Math.max(0.2, m.d)) * scale) / 2;
}

/**
 * The assembly's scale for a LANDMARK: the tier's presence height, its
 * footprint cap, and — with a park envelope — the park: the bounding circle
 * stays inside LANDMARK_PARK_SHARE of the envelope's shorter side, so the
 * monument stands IN the square with ground around it. `roomR` (a ring
 * fallback with no park) bounds the circle the same way.
 */
export function landmarkScale(key: string, tier: string, m: PrefabMetrics, room: number | null): number {
  const glbH = Math.max(0.2, m.h), glbWD = Math.max(0.2, m.w, m.d);
  let s = key.startsWith(CIV_PREFIX) ? Math.min((CIV_LANDMARK_H[tier] ?? 90) / glbH, (CIV_LANDMARK_LOT_W[tier] ?? 50) / glbWD) : LANDMARK_SCALE;
  if (room !== null && room > 0) {
    const maxR = (room * LANDMARK_PARK_SHARE) / 2;
    const r1 = landmarkRadius(m, 1);
    if (r1 > 0) s = Math.min(s, maxR / r1);
  }
  return s;
}

export interface Obb { x: number; z: number; hx: number; hz: number; yaw: number }

/** The drawn footprint's corners (three's rotation about +Y: local x → (cos, −sin), local z → (sin, cos)). */
export function obbCorners(o: Obb): number[][] {
  const c = Math.cos(o.yaw), s = Math.sin(o.yaw);
  const out: number[][] = [];
  for (const [ux, uz] of [[1, 1], [-1, 1], [-1, -1], [1, -1]]) { const x = ux * o.hx, z = uz * o.hz; out.push([o.x + x * c + z * s, o.z - x * s + z * c]); }
  return out;
}

/** Penetration depth of two footprints (0 when apart) — the separating-axis test. */
export function obbPenetration(a: Obb, b: Obb): number {
  if (Math.hypot(a.x - b.x, a.z - b.z) > Math.hypot(a.hx, a.hz) + Math.hypot(b.hx, b.hz)) return 0;
  const ca = obbCorners(a), cb = obbCorners(b);
  let depth = Infinity;
  for (const box of [a, b]) {
    const c = Math.cos(box.yaw), s = Math.sin(box.yaw);
    for (const [ax, az] of [[c, -s], [s, c]]) {
      let alo = Infinity, ahi = -Infinity, blo = Infinity, bhi = -Infinity;
      for (const [x, z] of ca) { const p = x * ax + z * az; if (p < alo) alo = p; if (p > ahi) ahi = p; }
      for (const [x, z] of cb) { const p = x * ax + z * az; if (p < blo) blo = p; if (p > bhi) bhi = p; }
      const ov = Math.min(ahi, bhi) - Math.max(alo, blo);
      if (ov <= 0) return 0;
      if (ov < depth) depth = ov;
    }
  }
  return depth;
}

/** Two footprints closer than `gap` (their boxes grown by gap/2 each) meet. */
export function obbsMeet(a: Obb, b: Obb, gap: number): boolean {
  const g = gap / 2;
  return obbPenetration({ ...a, hx: a.hx + g, hz: a.hz + g }, { ...b, hx: b.hx + g, hz: b.hz + g }) > 0;
}

export interface LandmarkSpot { lx: number; lz: number; room: number; pickIdx: number; yawIdx: number }
/** A ring spot must offer at least this much room (a village statue is ~4 m wide). */
export const LANDMARK_MIN_ROOM_M = 6;
/** A ring spot's room is capped: a plaza 270 m across is a SQUARE, and a monument keeps it open (≤ 0.72 × 90 / 2 = 32 m of radius). */
export const LANDMARK_RING_ROOM_MAX_M = 90;

/**
 * Where a settlement's landmarks stand (site-local), ONE rule for the assembly,
 * the far-tier bake and the layout audit: each PARK parcel's buildable envelope
 * (the park minus its roads' clearances; the first landmark pushed off-centre —
 * Sitte's monuments read against an edge), else — the park-less archetypes —
 * the ring spots around the core ordered by the ROOM they have before the
 * nearest carriageway (the old ring stood every hamlet's monument on its
 * street), never under `LANDMARK_MIN_ROOM_M`. `room` bounds the landmark's
 * scale (`landmarkScale`); `pickIdx`/`yawIdx` are the hash indices the callers
 * pick the model and yaw with (unchanged from the ring's 780/790 and the park's
 * parcel id, so nothing already baked changes its model).
 */
export function landmarkSpots(plan: CityPlan, siteSeed: number, cap: number, sideGap: number, buildable: (lx: number, lz: number) => boolean): LandmarkSpot[] {
  const out: LandmarkSpot[] = [];
  if (cap <= 0) return out;
  const parkRoads: LotRoad[] = plan.roads.edges.map((e) => ({ polyline: e.polyline, clear: e.halfWidth + plan.config.roads.sidewalkWidth + 2 }));
  for (const pc of plan.parcels) {
    if (out.length >= cap) break;
    if (pc.zone !== 'park') continue;
    const env = parcelEnvelope(pc.polygon, parkRoads, sideGap, 8);
    if (!env) continue; // a park too small for any monument stays green
    let lx = env.cx, lz = env.cz;
    const room = Math.min(env.w, env.d);
    if (out.length === 0) {
      const ang = h01(siteSeed, 777) * Math.PI * 2;
      const push = Math.min(18, room * 0.12);
      lx += Math.cos(ang) * push; lz += Math.sin(ang) * push;
    }
    if (!buildable(lx, lz)) continue;
    out.push({ lx, lz, room, pickIdx: pc.id, yawIdx: pc.id + 7 });
  }
  if (out.length > 0) return out;
  const roomAt = (lx: number, lz: number): number => {
    let best = Infinity;
    for (const e of plan.roads.edges) { const p = e.polyline; for (let i = 0; i + 3 < p.length; i += 2) { const d = segDist(lx, lz, p[i], p[i + 1], p[i + 2], p[i + 3]) - e.halfWidth - 2; if (d < best) best = d; } }
    return Math.max(0, best) * 2;
  };
  const baseAng = h01(siteSeed, 771) * Math.PI * 2;
  const cands: LandmarkSpot[] = [];
  const consider = (lx: number, lz: number, idx: number): void => {
    if (!buildable(lx, lz)) return;
    const room = Math.min(LANDMARK_RING_ROOM_MAX_M, roomAt(lx, lz));
    if (room < LANDMARK_MIN_ROOM_M) return;
    cands.push({ lx, lz, room, pickIdx: idx, yawIdx: idx + 10 });
  };
  // The classic ring (three radii on the golden angle), then a finer sweep of the same band: a hamlet's 18 m lattice
  // leaves no fixed radius 6 m clear of a road, but the middle of a block is.
  for (let li = 0; li < cap * 3; li++) { const ang = baseAng + li * 2.399963; const rr = 26 + (li % 3) * 22; consider(Math.cos(ang) * rr, Math.sin(ang) * rr, 780 + li); }
  for (let ri = 0; ri < 5; ri++) for (let ai = 0; ai < 24; ai++) { const rr = 22 + ri * 8, ang = baseAng + (ai / 24) * Math.PI * 2; consider(Math.cos(ang) * rr, Math.sin(ang) * rr, 900 + ri * 24 + ai); }
  cands.sort((a, b) => b.room - a.room || a.pickIdx - b.pickIdx);
  // Two monuments never share a block: keep spots ≥ 16 m apart.
  const out2: LandmarkSpot[] = [];
  for (const c of cands) { if (out2.length >= cap) break; if (out2.every((o) => Math.hypot(o.lx - c.lx, o.lz - c.lz) >= 16)) out2.push(c); }
  return out2;
}
